import React from 'react';
import { Box, Fade, Typography } from '@mui/material';

import { colors, COLORS } from '@constants/colors';
import { ScreenSizes } from '@interfaces';
import { useStreamStats } from '@core/dashboard/components/DevelopersTerminal/PixelStreamingTab/useStreamStats';
import { useMytaStreams } from './MytaStreamsProvider';

// The frames-per-second readout the myta-streams clients registry turns on PER CLIENT (2026-09-18): the UE team
// stream on their own lease tokens and want the number on screen, a production hub must never show it. Nothing
// here is a local preference — `showFps` arrives with every lease grant and renew, so an operator flips the
// switch on the Clients page and a session already in the venue picks it up within ~45 s.
//
// The number is what the BROWSER decoded (inboundVideoStats.framesPerSecond, the same WebRTC report the
// developers terminal reads). Deliberately not UE's render rate: this page sits behind the encoder and the
// network, and the only fps it can honestly claim is the one the person in the chair is actually being shown.
// The two differ exactly when it matters — a node rendering 60 while the viewer receives 24 is the case worth
// seeing, and a readout of "60" would hide it.

// Where the decoded rate stops being the stream working and starts being the stream struggling. 60 is the
// target (UE is launched at 60), 50 is a stream nobody would call broken, and under 30 the motion is visibly
// stepping. Colour only — the badge never pops up a warning, because a number the viewer cannot act on must
// not become an alarm.
const OK_FPS = 50;
const POOR_FPS = 30;

const fpsColor = (fps: number): string => {
  if (fps >= OK_FPS) {
    return colors.green;
  }

  return fps >= POOR_FPS ? COLORS.WARNING : colors.red;
};

const StreamFpsBadge = () => {
  const { showFps, pixelStreaming, playStream } = useMytaStreams();
  // Subscribed only while the badge is on screen: the WebRTC stats event fires every second, and a client that
  // is not showing the number has no reason to re-render for it.
  const { fps } = useStreamStats(showFps && playStream ? pixelStreaming : null);

  if (!showFps || !playStream) {
    return null;
  }

  // Before the first stats report there is no honest number to put up — an empty badge reads as a stream at
  // zero. The picture is already on screen by then, so the badge simply arrives a second into the session.
  if (fps === null) {
    return null;
  }

  const rounded = Math.round(fps);

  return (
    <Fade in appear timeout={220}>
      <Box
        data-testid="myta-fps-badge"
        sx={(theme) => ({
          position: 'fixed',
          // Top-LEFT, clear of the chrome row the spot notice owns (top-centre) and of the fullscreen controls
          // (top-right): three things fighting for the same strip is how a diagnostic ends up covering a message
          // that actually needs reading.
          top: '96px',
          left: '16px',
          zIndex: 1400,
          // A readout, never a target: it must not swallow a click meant for the world behind it.
          pointerEvents: 'none',
          display: 'flex',
          alignItems: 'baseline',
          gap: '5px',
          padding: '3px 10px',
          borderRadius: '99px',
          background: colors.black_99,
          backdropFilter: 'blur(12px)',
          border: `1px solid ${colors.white_20}`,
          [theme.breakpoints.down(ScreenSizes.TabletHorizontal)]: {
            top: '72px',
          },
        })}
      >
        {/* No tooltip: the badge is click-through by design, so a hover target here would never fire — and a
            diagnostic that has to be hovered to be understood is the wrong diagnostic. */}
        <Typography
          sx={{
            fontSize: '13px',
            fontWeight: 700,
            lineHeight: 1.3,
            fontVariantNumeric: 'tabular-nums',
            color: fpsColor(rounded),
          }}
        >
          {rounded}
        </Typography>
        <Typography
          sx={{
            fontSize: '10px',
            fontWeight: 600,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: colors.white_60,
          }}
        >
          fps
        </Typography>
      </Box>
    </Fade>
  );
};

export default StreamFpsBadge;
